import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChartJS from "chart.js/auto";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { IoSearchOutline } from "react-icons/io5";
import AdminDashboard from "./admindashboard";

ChartJS.register(ChartDataLabels);

function AdminAnalytics() {
  const navigate = useNavigate();
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [labs, setLabs] = useState([]);
  const [executed, setExecuted] = useState({ date: "" });
  const [loader, setLoader] = useState(false);

  const getCurrentMonth = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${year}-${month}`;
  };

  const currentDate1 = getCurrentMonth();

  const getData = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      setLoader(true);

      const labRes = await axios.get(`/api/labdetails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabs(labRes.data);

      const res = await axios.get(`/api/getAppointmentsByMonth/date=${currentDate1}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppointmentsData(res.data);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  useEffect(() => {
    getData();
  }, []);

  const searchByMonth = async (e, text) => {
    e.preventDefault();
    const token = JSON.parse(localStorage.getItem("data")).token;

    try {
      const res = await axios.get(`/api/getAppointmentsByMonth/date=${text}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppointmentsData(res.data);
    } catch (error) {
      handleError(error);
    }
  };

  const rhandle = (e, field) => {
    e.preventDefault();
    let actualValue = e.target.value;
    setExecuted((prevState) => ({ ...prevState, [field]: actualValue }));
  };

  // Process data for chart
  const processedData = labs.map((lab) => {
    const matchingAppointments = appointmentsData.find(
      (appointment) => appointment.labId === lab.id
    );
    return {
      labname: `${lab.labname} (${lab.labcode})`,
      totalAppointments: matchingAppointments ? matchingAppointments.totalAppointments : 0,
      totalAppExecuted: matchingAppointments ? matchingAppointments.totalAppExecuted : 0,
    };
  });

  // Dynamic height calculation: 50px per lab with a minimum height of 400px
  const chartHeight = Math.max(400, processedData.length * 50);

  return (
    <div>
      <AdminDashboard />
      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">bar_chart</i>
                Appointment Analytics
              </h2>
              <p className="hm-page-subtitle">Monthly lab-wise appointment execution overview</p>
            </div>
          </div>

          {/* Analytics Chart Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">analytics</i>
                Total Executed Appointments
              </h3>
              <div className="d-flex align-items-center gap-2">
                <input
                  id="date"
                  type="month"
                  className="form-control"
                  style={{ width: "180px" }}
                  value={executed.date || getCurrentMonth()}
                  onChange={(e) => rhandle(e, "date")}
                />
                <button
                  type="button"
                  className="btn btn-primary rounded-pill px-4"
                  id="btn"
                  onClick={(e) => searchByMonth(e, executed.date)}
                >
                  <IoSearchOutline id="IoSearchOutline" style={{ marginRight: '4px' }} />
                  Search
                </button>
              </div>
            </div>

            <div className="hm-card-body">
              {processedData.length === 0 ? (
                <div className="hm-empty-state">
                  <i className="material-icons">bar_chart</i>
                  <p>No appointment data available for the selected month.</p>
                </div>
              ) : (
                <div
                  style={{ position: "relative", height: `${chartHeight}px`, width: "100%" }}
                >
                  <Bar
                    data={{
                      labels: processedData.map((item) => item.labname),
                      datasets: [
                        {
                          label: "Executed Appointments",
                          data: processedData.map((item) => item.totalAppExecuted),
                          backgroundColor: 'rgba(14, 165, 233, 0.75)',
                          borderRadius: 6,
                        },
                        {
                          label: "Pending Appointments",
                          data: processedData.map((item) => item.totalAppointments - item.totalAppExecuted),
                          backgroundColor: 'rgba(99, 102, 241, 0.6)',
                          borderRadius: 6,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      scales: {
                        x: {
                          stacked: true,
                          beginAtZero: true,
                          max: Math.max(...processedData.map(item => item.totalAppointments), 1) + 5,
                          grid: {
                            color: 'rgba(148, 163, 184, 0.15)',
                          },
                          ticks: { color: '#64748b' },
                        },
                        y: {
                          stacked: true,
                          beginAtZero: true,
                          grid: { display: false },
                          ticks: { color: '#64748b', font: { size: 12 } },
                        },
                      },
                      barPercentage: 0.6,
                      categoryPercentage: 0.8,
                      plugins: {
                        legend: {
                          labels: { color: '#64748b', padding: 20 },
                        },
                        datalabels: {
                          display: true,
                          color: 'white',
                          font: { weight: 'bold', size: 11 },
                          formatter: (value, context) => {
                            if (context.dataset.label === 'Pending Appointments') {
                              const pendingApp = value;
                              const executedApp = context.chart.data.datasets[0].data[context.dataIndex];
                              return pendingApp + executedApp;
                            }
                            return value > 0 ? value : '';
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              let label = context.dataset.label || '';
                              if (label) label += ': ';
                              label += context.formattedValue;
                              if (context.dataset.label === 'Pending Appointments') {
                                const totalApp = context.raw;
                                const executedApp = context.chart.data.datasets[0].data[context.dataIndex];
                                label = `Total Appointments: ${totalApp + executedApp}`;
                              }
                              return label;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
      {loader && <div className="loading"></div>}
    </div>
  );
}

export default AdminAnalytics;
